// SurveyEditor.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Box from '@mui/material/Box';
import { SurveyCreatorComponent, SurveyCreator } from 'survey-creator-react';
import 'survey-core/defaultV2.min.css';
import 'survey-creator-core/survey-creator-core.min.css';
import { setLicenseKey, SvgRegistry, Action } from 'survey-core';
import useApi from '../hooks/useApi';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import useSnackbar from '../hooks/useSnackbar';
import './surveyComponents/css/surveyTheme.css';
import themeJson from './surveyComponents/surveyTheme';
import { ReactElementFactory } from 'survey-react-ui';
import SurveySendDialog from './surveyComponents/SurveySendDialog';
import SurveyTopicsDialog from './surveyComponents/SurveyTopicsDialog';
import buildUrl from '../buildUrl';
import config from '../config';

const { SURVEY_API_KEY } = config;
setLicenseKey(SURVEY_API_KEY);

const customIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" title="ScreenRotation" width="24" height="24"><path d="M16.48 2.52c3.27 1.55 5.61 4.72 5.97 8.48h1.5C23.44 4.84 18.29 0 12 0l-.66.03 3.81 3.81zm-6.25-.77c-.59-.59-1.54-.59-2.12 0L1.75 8.11c-.59.59-.59 1.54 0 2.12l12.02 12.02c.59.59 1.54.59 2.12 0l6.36-6.36c.59-.59.59-1.54 0-2.12zm4.6 19.44L2.81 9.17l6.36-6.36 12.02 12.02zm-7.31.29C4.25 19.94 1.91 16.76 1.55 13H.05C.56 19.16 5.71 24 12 24l.66-.03-3.81-3.81z" /></svg>';
SvgRegistry.registerIconFromSvg('icon-device-rotate', customIcon);

const creatorOptions = {
    showLogicTab: true,
    isAutoSave: true,
    showDesignerTab: true,
    showTestSurveyTab: true,
    showTranslationTab: false,
    showJSONEditorTab: false,
    showThemeTab: true,
    questionTypes: ['text', 'checkbox', 'radiogroup', 'dropdown', 'boolean', 'comment', 'tagbox', 'image', 'matrix', 'matrixdropdown', 'matrixdynamic', 'multipletext', 'panel', 'paneldynamic', 'rating', 'ranking']
};

const SurveyEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [survey, setSurvey] = useState(null);
    const { handleGet, handlePut, handlePost } = useApi();
    const [isLoading, setIsLoading] = useState(true);
    const [showSendDialog, setShowSendDialog] = useState(false);
    const [showTopicsDialog, setShowTopicsDialog] = useState(false);
    const { showSnackbar } = useSnackbar();

    const creatorRef = useRef(null);
    const surveyRef = useRef(null);

    useEffect(() => { surveyRef.current = survey; }, [survey]);

    const handleLoadSurvey = async () => {
        const response = await handleGet(`surveys/${id}`);

        if (!response.ok) {
            console.error(response?.error);
            navigate('/editor', { state: { type: 'survey' } });
            return;
        }

        const data = await response.json();

        setSurvey(data);
        setIsLoading(false);
    };

    const handleSaveSurveyJson = async (json, saveNumber) => {
        const response = await handlePut(`surveys/${id}`, { json: JSON.stringify(json) });

        if (!response.ok) {
            console.error(response.statusText, saveNumber);
            showSnackbar('Cannot save survey', 'error');
            return;
        }

        setSurvey(prev => ({ ...(prev ?? {}), json: JSON.stringify(json) }));
    };

    const saveSurveyTheme = async (theme, saveNumber) => {
        const response = await handlePut(`surveys/${id}`, { theme: JSON.stringify(theme) });

        if (!response.ok) {
            console.error(response.statusText, saveNumber);
            showSnackbar('Cannot save survey', 'error');
            return;
        }

        setSurvey(prev => ({ ...(prev ?? {}), theme: JSON.stringify(theme) }));
    };

    const handleSendSurvey = async () => {
        setIsLoading(true);

        try {
            const templateUrl = buildUrl('templates', { type: 'survey' });
            const template = await handleGet(templateUrl);

            if (!template.ok) {
                showSnackbar('Error fetching template', 'error');
                setIsLoading(false);
                return;
            }

            const templateData = await template.json();

            const response = await handlePost('/emails', {
                surveyId: surveyRef.current.id,
                name: surveyRef.current.name,
                templateId: templateData[0].id,
                type: 'survey'
            });
            const data = await response.json();

            if (!response.ok) {
                showSnackbar('Error sending survey', 'error');
            }
            else {
                navigate(`/editor/email/${data.id}`);
            }
        }
        catch {
            showSnackbar('An unexpected error occurred', 'error');
        }
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (creatorRef.current) return;

        const c = new SurveyCreator(creatorOptions);

        c.saveSurveyFunc = (n) => handleSaveSurveyJson(c.JSON, n);
        c.saveThemeFunc = (n) => saveSurveyTheme(c.theme, n);

        ReactElementFactory.Instance.registerElement('svc-nz-dashboard', () => <span style={{ padding: 8 }} />);

        const dashPlugin = {
            activate: () => {
                navigate('/editor', { state: { type: 'survey' } });
                return true;
            },
            deactivate: () => true
        };

        c.addPluginTab('nz-dashboard', dashPlugin, 'Dashboard', 'svc-nz-dashboard', 0);

        const topicsAction = new Action({
            id: 'nz-topics',
            title: 'Topics',
            action: () => setShowTopicsDialog(true)
        });
        const copyLinkAction = new Action({
            id: 'nz-copy-link',
            title: 'Copy Link',
            action: () => {
                const s = surveyRef.current;
                if (!s?.id || !s?.verifier) return;
                const baseUrl = window.location.origin;
                const link = `${baseUrl}/#/surveys/${s.id}/${s.verifier}`;
                navigator.clipboard.writeText(link);
                showSnackbar('Link copied to clipboard', 'success');
            }
        });
        const distributeAction = new Action({
            id: 'nz-distribute',
            title: 'Distribute',
            action: () => setShowSendDialog(true)
        });

        const actions = c.toolbar.actions;
        const undoIdx = actions.findIndex(
            a => (a.id || '').toLowerCase().includes('undo') || (a.title || '').toLowerCase() === 'undo'
        );
        const insertAt = undoIdx >= 0 ? undoIdx : actions.length;

        actions.splice(insertAt, 0, topicsAction, copyLinkAction, distributeAction);

        creatorRef.current = c;
    }, [history, showSnackbar]);

    useEffect(() => {
        const c = creatorRef.current;
        if (!c || !survey) return;

        try {
            c.theme = survey.theme ? JSON.parse(survey.theme) : themeJson;
        }
        catch {
            c.theme = themeJson;
        }

        try {
            if (typeof survey.json === 'string' && survey.json?.trim()) {
                c.text = survey.json;
            }
            else if (survey.json) {
                c.JSON = survey.json;
            }
            else {
                c.JSON = { pages: [] };
            }
        }
        catch {
            c.JSON = { pages: [] };
        }
    }, [survey]);

    useEffect(() => {
        setIsLoading(true);
        handleLoadSurvey();
    }, [id]);

    if (isLoading || !creatorRef.current) {
        return <LoadingOverlay />;
    }

    return (
        <>
            <Box sx={{ height: 'calc(100vh - 64px)' }}>
                <SurveyCreatorComponent creator={creatorRef.current} />
            </Box>

            {showTopicsDialog && (
                <SurveyTopicsDialog
                    open={showTopicsDialog}
                    onClose={() => setShowTopicsDialog(false)}
                    surveyTopics={survey?.topics}
                    id={survey?.id}
                />
            )}

            {showSendDialog && (
                <SurveySendDialog
                    open={showSendDialog}
                    onHide={() => setShowSendDialog(false)}
                    data={survey}
                    onSend={handleSendSurvey}
                    isLoading={isLoading}
                />
            )}
        </>
    );
};

export default SurveyEditor;